import { useState, useEffect, useMemo, useRef } from 'react';
import Form from "../Form/Form";
import Button from '../Button/Button';
import { strapiApi } from '../../services/api';
import style from './onlinerecordingform.module.scss';
import { useAccessibility } from '../../context/AccessibilityContext/AccessibilityContext';

const OnlineRecordingForm = ({ program, availableBranches = [], onSuccess }) => {
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        age: '',
        branch: ''
    });
    const [formErrors, setFormErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [lastSubmitTime, setLastSubmitTime] = useState(0);
    const { settings } = useAccessibility();

    // Ref для таймера
    const submitTimerRef = useRef(null);

    // Memoize branch options to prevent unnecessary recalculations
    const branchOptions = useMemo(() => {
        return availableBranches.map(branch => ({
            value: branch.id,
            label: `${branch.Title || 'Филиал'} (${branch.Address || ''})`
        }));
    }, [availableBranches]);

    // Получаем название выбранного филиала
    const getSelectedBranchTitle = (branchId) => {
        const branch = availableBranches.find(b => b.id === branchId);
        return branch ? branch.Title : '';
    };

    // Валидация формы
    const validateForm = () => {
        const errors = {};

        // Валидация имени
        if (!formData.name.trim()) {
            errors.name = 'Имя обязательно для заполнения';
        } else if (formData.name.trim().length < 2) {
            errors.name = 'Имя должно содержать минимум 2 символа';
        }

        // Валидация телефона
        if (!formData.phone.trim()) {
            errors.phone = 'Телефон обязателен для заполнения';
        } else {
            const phoneRegex = /^[\+]?[78]?[-\s]?\(?\d{3}\)?[-\s]?\d{3}[-\s]?\d{2}[-\s]?\d{2}$/;
            if (!phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
                errors.phone = 'Введите корректный номер телефона';
            }
        }

        // Валидация email (если заполнен)
        if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Введите корректный email адрес';
        }

        // Валидация возраста
        if (!formData.age.trim()) {
            errors.age = 'Возраст обязателен для заполнения';
        } else {
            const ageNum = parseInt(formData.age);
            if (isNaN(ageNum)) {
                errors.age = 'Возраст должен быть числом';
            } else if (ageNum < 3 || ageNum > 100) {
                errors.age = 'Возраст должен быть от 3 до 100 лет';
            }
        }

        // Валидация филиала
        if (!formData.branch && branchOptions.length > 0) {
            errors.branch = 'Выберите филиал';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Очистка ошибки при изменении поля
    const clearError = (fieldName) => {
        setFormErrors(prev => ({
            ...prev,
            [fieldName]: ''
        }));
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Очищаем ошибку при изменении поля
        if (formErrors[field]) {
            clearError(field);
        }
    };

    // Форматирование телефона
    const formatPhone = (phone) => {
        return phone.replace(/\D/g, '');
    };

    const submitApplication = async (formData) => {
        // Проверяем время последней отправки (минимум 5 секунд между запросами)
        const now = Date.now();
        const timeSinceLastSubmit = now - lastSubmitTime;
        
        if (timeSinceLastSubmit < 5000) {
            alert('Пожалуйста, подождите 5 секунд перед повторной отправкой заявки');
            return;
        }

        // Валидация формы
        if (!validateForm()) {
            alert('Пожалуйста, исправьте ошибки в форме');
            return;
        }

        setIsLoading(true);
        setLastSubmitTime(now);

        console.log('Submitting application with data:', formData);
        console.log('Program:', program);

        try {
            // Исправленный формат данных согласно бэкенду
            const requestData = {
                data: {
                    attributes: {
                        Name: formData.name.trim(),
                        Phone: formatPhone(formData.phone),
                        Email: formData.email.trim(),
                        Age: parseInt(formData.age),
                        programTitle: program?.Title || program?.title || 'Не указано',
                        branchTitle: getSelectedBranchTitle(formData.branch) || 'Не указано',
                        status: 'new'
                    }
                }
            };

            console.log('Sending request data:', requestData);

            await strapiApi.post('/api/applications', requestData);
            
            setIsSubmitted(true);
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error('Error submitting application:', error);
            const errorMessage = error.response?.data?.error || 
                               error.response?.data?.message || 
                               'Произошла ошибка при отправке заявки';
            alert(errorMessage);
            
            // Сбрасываем таймер при ошибке
            setLastSubmitTime(0);
        } finally {
            setIsLoading(false);
        }
    };

    // Очистка таймера при размонтировании
    useEffect(() => {
        return () => {
            if (submitTimerRef.current) {
                clearTimeout(submitTimerRef.current);
            }
        };
    }, []);

    // Accessibility-aware colors
    const containerBg = settings.contrast === 'default' ? '#fff' : 'var(--bg-primary)';
    const textColor = settings.contrast === 'default' ? '#1d3e8c' : 'var(--text-primary)';
    const borderColor = settings.contrast === 'default' ? '#1d3e8c' : 'var(--text-primary)';
    const errorColor = settings.contrast === 'default' ? '#e53e3e' : '#ff6b6b';
    const inputBg = settings.contrast === 'default' ? '#fff' : 'var(--bg-secondary)';
    const buttonBg = settings.contrast === 'default' ? '#1d3e8c' : 'var(--accent-color)';
    const disabledButtonBg = settings.contrast === 'default' ? '#a0aec0' : 'var(--border-color)';

    // Проверяем, можно ли отправить форму
    const canSubmit = !isLoading && 
                     branchOptions.length > 0 && 
                     (Date.now() - lastSubmitTime) >= 5000;

    const inputs = useMemo(() => ({
        name: {
            name: 'Имя',
            width: '100%',
            height: '50px',
            margin: '0 0 5px 0',
            placeholder: 'Имя*',
            background: inputBg,
            color: textColor,
            border: formErrors.name ? `2px solid ${errorColor}` : `1px solid ${borderColor}`,
            borderRadius: '8px',
            value: formData.name,
            setValue: (value) => handleInputChange('name', value),
            required: true,
        },
        phone: {
            name: 'Телефон',
            width: '100%',
            height: '50px',
            margin: '0 0 5px 0',
            placeholder: 'Телефон* (например: 8-999-123-45-67)',
            background: inputBg,
            color: textColor,
            border: formErrors.phone ? `2px solid ${errorColor}` : `1px solid ${borderColor}`,
            borderRadius: '8px',
            value: formData.phone,
            setValue: (value) => handleInputChange('phone', value),
            required: true,
        },
        email: {
            name: 'Email',
            width: '100%',
            height: '50px',
            margin: '0 0 5px 0',
            placeholder: 'E-mail (необязательно)',
            background: inputBg,
            color: textColor,
            border: formErrors.email ? `2px solid ${errorColor}` : `1px solid ${borderColor}`,
            borderRadius: '8px',
            value: formData.email,
            setValue: (value) => handleInputChange('email', value),
        },
        age: {
            name: 'Возраст',
            width: '100%',
            height: '50px',
            margin: '0 0 5px 0',
            placeholder: 'Возраст ребенка* (от 3 до 100 лет)',
            background: inputBg,
            color: textColor,
            border: formErrors.age ? `2px solid ${errorColor}` : `1px solid ${borderColor}`,
            borderRadius: '8px',
            value: formData.age,
            setValue: (value) => {
                // Разрешаем только цифры
                const numericValue = value.replace(/\D/g, '');
                handleInputChange('age', numericValue);
            },
            required: true,
            type: 'tel', // Для мобильной клавиатуры с цифрами
            inputMode: 'numeric', // Для мобильных устройств
            pattern: '[0-9]*' // Для HTML5 валидации
        },
        branch: {
            name: 'Филиал',
            type: 'select',
            width: '100%',
            height: '50px',
            margin: '0 0 5px 0',
            placeholder: branchOptions.length ? 'Выберите филиал*' : 'Нет доступных филиалов',
            background: inputBg,
            color: textColor,
            border: formErrors.branch ? `2px solid ${errorColor}` : `1px solid ${borderColor}`,
            borderRadius: '8px',
            value: formData.branch,
            setValue: (value) => handleInputChange('branch', value),
            required: true,
            options: branchOptions,
            disabled: branchOptions.length === 0
        }
    }), [formData, formErrors, branchOptions, inputBg, textColor, borderColor, errorColor]);

    // Компонент для отображения ошибок
    const ErrorMessage = ({ error }) => {
        if (!error) return null;
        
        return (
            <div 
                className={style.errorMessage}
                style={{ color: errorColor }}
                role="alert"
                aria-live="polite"
            >
                {error}
            </div>
        );
    };

    if (isSubmitted) {
        return (
            <div className={style.successContainer} style={{ background: containerBg }}>
                <div className={style.successContent}>
                    <svg className={style.successIcon} viewBox="0 0 24 24">
                        <path fill="#4CAF50" d="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z"/>
                    </svg>
                    <h3 className={style.successTitle} style={{ color: textColor }}>Заявка принята!</h3>
                    <p className={style.successText} style={{ color: textColor }}>
                        Мы свяжемся с вами в ближайшее время для подтверждения записи.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={style.formContainer} style={{ background: containerBg }}>
            <h3 className={style.formTitle} style={{ color: textColor }}>ЗАПИСАТЬСЯ НА ТРЕНИРОВКУ</h3>
            {program && (
                <div className={style.programInfo} style={{ color: textColor }}>
                    <strong>Программа:</strong> {program.Title || program.title}
                </div>
            )}
            
            <Form
                className={style.feedbackform}
                inputs={inputs}
                onSubmit={() => submitApplication(formData)}
                button={
                    <div className={style.buttonContainer}>
                        <Button
                            type="submit"
                            width="100%"
                            height="50px"
                            text={isLoading ? 'ОТПРАВКА...' : 'ОТПРАВИТЬ ЗАЯВКУ'}
                            fontFamily="Roboto, sans-serif"
                            fontSize="1rem"
                            fontWeight="600"
                            background={canSubmit ? buttonBg : disabledButtonBg}
                            color="#fff"
                            border="none"
                            disabled={!canSubmit}
                        />
                        {!canSubmit && (Date.now() - lastSubmitTime) < 5000 && (
                            <div 
                                className={style.cooldownMessage}
                                style={{ color: textColor }}
                            >
                                Следующую заявку можно отправить через {Math.ceil((5000 - (Date.now() - lastSubmitTime)) / 1000)} сек.
                            </div>
                        )}
                    </div>
                }
            />
            
            {/* Отображение ошибок */}
            <div className={style.errorsContainer}>
                <ErrorMessage error={formErrors.name} />
                <ErrorMessage error={formErrors.phone} />
                <ErrorMessage error={formErrors.email} />
                <ErrorMessage error={formErrors.age} />
                <ErrorMessage error={formErrors.branch} />
            </div>
        </div>
    );
};

export default OnlineRecordingForm;